import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import type { Country } from "react-phone-number-input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

const SIGNUP_COUNTRIES: Country[] = ["US", "CA"];

function normalizeInstagramUsername(value: string): string {
  return value.trim().replace(/^@+/, "");
}

export function Signup() {
  const [phone, setPhone] = useState<string | undefined>();
  const [instagramUsername] = useState(() => {
    const ig = new URLSearchParams(window.location.search).get("ig");
    if (!ig) return undefined;
    const username = normalizeInstagramUsername(ig);
    return username || undefined;
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    clearBreadcrumbs();
  }, []);

  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has("ig")) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("ig");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const country = phone ? parsePhoneNumber(phone)?.country : undefined;
    if (
      !phone ||
      !isValidPhoneNumber(phone) ||
      !country ||
      !SIGNUP_COUNTRIES.includes(country)
    ) {
      setError("Please enter a valid US or Canada phone number");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("/api/users/signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          phone,
          ...(instagramUsername ? { instagramUsername } : {}),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        navigate("/onboarding");
      } else {
        setError(data.error || "Signup failed");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      console.error("Signup error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto flex min-h-screen flex-col items-center p-8">
      <div className="flex w-full flex-1 flex-col items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Sign Up</CardTitle>
            <CardDescription>Enter your phone number to get started</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <PhoneInput
                  id="phone"
                  international
                  defaultCountry="US"
                  countries={SIGNUP_COUNTRIES}
                  value={phone}
                  onChange={setPhone}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
                />
              </div>
              {error && (
                <div className="text-sm text-red-500">{error}</div>
              )}
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Creating account..." : "Sign Up"}
              </Button>
            </form>
            <p className="text-center text-sm text-muted-foreground mt-6">
              Already have an account?{" "}
              <Link to="/login" className="font-medium text-primary underline underline-offset-4">
                Log in
              </Link>
            </p>
            <p className="text-center text-sm text-muted-foreground mt-4">
              By continuing, you agree to our{" "}
              <Link to="/terms" className="underline hover:text-foreground">
                Terms
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="underline hover:text-foreground">
                Privacy Policy
              </Link>
              .
            </p>
          </CardContent>
        </Card>
      </div>
      <footer className="flex flex-col items-center gap-1 text-center">
        <p className="text-xs text-muted-foreground">Engram (formerly Kaizen Place)</p>
        <p className="text-xs text-muted-foreground">
          <a
            href="mailto:hello@engram.sh"
            className="font-medium text-primary underline underline-offset-4"
          >
            Contact Us
          </a>
        </p>
      </footer>
    </div>
  );
}
